import { File, Image, Link, X } from 'lucide-react';
import { Button } from '../ui/button';
import type { Attachment } from '@runloop/reflex-contract';
import { cn } from '../../lib/utils';

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function getHostname(url: string): string {
  try {
    return new URL(url).hostname;
  } catch {
    return url;
  }
}

const iconByType = {
  file: File,
  image: Image,
  link: Link,
} as const;

interface AttachmentChipProps {
  attachment: Attachment;
  onRemove?: () => void;
  onClick?: () => void;
}

export function AttachmentChip({ attachment, onRemove, onClick }: AttachmentChipProps) {
  const Icon = iconByType[attachment.type];

  const label =
    attachment.type === 'link' && attachment.url ? getHostname(attachment.url) : attachment.name;

  const sizeLabel = attachment.size != null ? formatSize(attachment.size) : null;

  return (
    <span
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onClick={onClick}
      onKeyDown={
        onClick
          ? (e) => {
              if (e.key === 'Enter') onClick();
            }
          : undefined
      }
      className={cn(
        'inline-flex max-w-[280px] items-center gap-1.5 rounded-md bg-muted px-2 py-1 text-xs text-foreground',
        onClick &&
          'cursor-pointer hover:bg-muted/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
      )}
    >
      <Icon className="h-3 w-3 shrink-0 text-muted-foreground" />
      <span className="truncate">{label}</span>
      {sizeLabel && <span className="shrink-0 text-muted-foreground">{sizeLabel}</span>}
      {onRemove && (
        <Button
          variant="ghost"
          size="icon-xs"
          onClick={(e) => {
            e.stopPropagation();
            onRemove();
          }}
          className="ml-0.5"
          // Name the attachment: a message can carry several chips, and
          // "Remove attachment" ×3 is unusable with a screen reader.
          aria-label={`Remove ${label}`}
        >
          <X className="h-3 w-3" />
        </Button>
      )}
    </span>
  );
}
